import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ClienteSelect } from '../../../shared/components/ClienteSelect';
import { ColaboradorSelect } from '../../../shared/components/ColaboradorSelect';
import { MonthFilterField, fromMonthValue, toMonthValue } from '../../../shared/components/MonthFilterField';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { createEmployeeNotasFiscaisApi, fetchNotaFiscalEspelho } from '../notas-fiscais/api';
import type { NotaFiscalEspelho } from '../notas-fiscais/types';

export interface NotaFiscalColaboradorNovaDialogProps {
  open: boolean;
  onClose: () => void;
}

function money(value: number | null | undefined): string {
  const n = Number(value);
  return (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function hours(value: number | null | undefined): string {
  const n = Number(value);
  return (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {rotulo}
      </Typography>
      <Typography variant="body2">{valor}</Typography>
    </Stack>
  );
}

export function NotaFiscalColaboradorNovaDialog({
  open,
  onClose,
}: NotaFiscalColaboradorNovaDialogProps): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const now = new Date();
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [clienteCodigo, setClienteCodigo] = useState<number | null>(null);
  const [periodo, setPeriodo] = useState(toMonthValue(now.getFullYear(), now.getMonth() + 1));

  useEffect(() => {
    if (!open) return;
    const hoje = new Date();
    setEmployeeId(null);
    setClienteCodigo(null);
    setPeriodo(toMonthValue(hoje.getFullYear(), hoje.getMonth() + 1));
  }, [open]);

  const { year, month } = fromMonthValue(periodo);
  const pronto = employeeId != null && clienteCodigo != null && clienteCodigo > 0 && year != null && month != null;

  const espelhoQuery = useQuery({
    queryKey: ['notas-fiscais', 'espelho', employeeId, clienteCodigo, year, month],
    queryFn: () =>
      fetchNotaFiscalEspelho(employeeId as number, {
        clienteCodigo: clienteCodigo as number,
        mes: month as number,
        ano: year as number,
      }),
    enabled: open && pronto,
  });

  const criar = useMutation({
    mutationFn: () =>
      createEmployeeNotasFiscaisApi(employeeId as number).create({
        clienteCodigo: clienteCodigo as number,
        mes: month as number,
        ano: year as number,
      }),
    onSuccess: async () => {
      toast.success(t('features.colaboradores.notasFiscaisGlobal.nova.criada'));
      await qc.invalidateQueries({ queryKey: ['notas-fiscais', 'global'] });
      onClose();
    },
    onError: (err) => toast.error(describeApiError(err)),
  });

  const base = 'features.colaboradores.notasFiscaisGlobal.nova';
  const espelho = espelhoQuery.data;

  return (
    <Dialog open={open} onClose={criar.isPending ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t(`${base}.title`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <ColaboradorSelect
            value={employeeId}
            onChange={(id) => {
              setEmployeeId(id);
              setClienteCodigo(null);
            }}
            label={t(`${base}.colaborador`)}
          />
          <ClienteSelect
            value={clienteCodigo}
            onChange={(id) => setClienteCodigo(id)}
            label={t(`${base}.cliente`)}
            disabled={employeeId == null}
            apontavelParaColaborador={employeeId}
          />
          <MonthFilterField
            fullWidth
            label={t('components.monthFilter.competencia')}
            value={periodo}
            onChange={setPeriodo}
          />

          {!pronto && (
            <Typography variant="body2" color="text.secondary">
              {t(`${base}.aguardando`)}
            </Typography>
          )}
          {pronto && espelhoQuery.isFetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {pronto && espelhoQuery.isError && (
            <Typography variant="body2" color="error">
              {describeApiError(espelhoQuery.error)}
            </Typography>
          )}
          {espelho && !espelhoQuery.isFetching && <EspelhoPainel espelho={espelho} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={criar.isPending}>
          {t(`${base}.cancelar`)}
        </Button>
        <Button
          variant="contained"
          disabled={!espelho || espelhoQuery.isFetching || espelhoQuery.isError || criar.isPending}
          onClick={() => criar.mutate()}
        >
          {t(`${base}.confirmar`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EspelhoPainel({ espelho }: { espelho: NotaFiscalEspelho }): JSX.Element {
  const { t } = useTranslation();
  const base = 'features.colaboradores.notasFiscaisGlobal.nova';
  const tipo =
    espelho.tipoPagamento === 'F'
      ? t(`${base}.fixo`)
      : espelho.tipoPagamento === 'H'
        ? t(`${base}.horista`)
        : espelho.tipoPagamento;

  const horasValor: Array<[string, number, number]> = [
    [`${base}.horasNormais`, espelho.apontamentoTotalHoraPadrao, espelho.apontamentoValorHoraPadrao],
    [`${base}.horasAdicionais`, espelho.apontamentoTotalHoraAdicional, espelho.apontamentoValorHoraAdicional],
    [`${base}.horasPlantao`, espelho.apontamentoTotalHoraPlantao, espelho.apontamentoValorHoraPlantao],
    [`${base}.horasAcionamento`, espelho.apontamentoTotalHoraAcionamento, espelho.apontamentoValorHoraAcionamento],
  ];

  return (
    <Stack spacing={2}>
      <Linha rotulo={t(`${base}.status`)} valor={t(`${base}.naoSolicitada`)} />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t(`${base}.previsto`)}
        </Typography>
        <Stack spacing={0.75}>
          <Linha rotulo={t(`${base}.horas`)} valor={hours(espelho.espelhoTotalHoras)} />
          <Linha rotulo={t(`${base}.valor`)} valor={money(espelho.espelhoValor)} />
          <Linha rotulo={t(`${base}.tipoPagamento`)} valor={tipo} />
          <Linha rotulo={t(`${base}.valorHora`)} valor={money(espelho.valorHoraPagar)} />
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          {t(`${base}.apontamento`)}
        </Typography>
        <Stack spacing={0.75}>
          {horasValor.map(([key, h, v]) => (
            <Linha key={key} rotulo={t(key)} valor={`${hours(h)} · ${money(v)}`} />
          ))}
          <Divider />
          <Linha rotulo={t(`${base}.totalHoras`)} valor={hours(espelho.apontamentoTotalHoras)} />
          <Linha rotulo={t(`${base}.valorNota`)} valor={money(espelho.apontamentoValor)} />
        </Stack>
      </Paper>
    </Stack>
  );
}
